import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { darkenHexColor, lightenHexColor } from "../../utilities/parsers";

// All measurements below are in the SVG's own design-space units (the
// original artwork was authored on a 360x160 canvas with a 25px label).
// Everything scales off of that reference.
const BASE_FONT_SIZE = 25;
const DESIGN_WIDTH = 360;
const DESIGN_HEIGHT = 160;
const TEXT_CENTER_X = 160;
const CONTENT_TOP = 37;

// The label reads a touch small at the badge's native display size, so its
// glyphs are drawn slightly boosted relative to BASE_FONT_SIZE. The ribbon
// band (and its drop-shadow layer) grows a matching amount, split evenly
// top/bottom around the same centerline, so the bigger label still has room.
const TEXT_SIZE_BOOST = 1.15;
const TEXT_FONT_SIZE = BASE_FONT_SIZE * TEXT_SIZE_BOOST;
const BAND_GROWTH = 6;

// The skull sits inside the ribbon's right portion, so the label's safe
// corridor runs from just past the left cap to the skull's (moving) left
// edge - this is how much room it offers before the ribbon needs to stretch.
const TEXT_SAFE_WIDTH = 185;
const SIDE_PADDING = 14;

// The badge only ever grows/shrinks in exact proportion to the configured
// font size (never distorts), clamped so a stray value can't blow up the layout.
const MIN_FONT_SCALE = 0.5;
const MAX_FONT_SCALE = 2.5;

// Native display size of the artwork at BASE_FONT_SIZE, before font scaling.
const DISPLAY_SCALE = 0.35;

// Extra px nudge to keep the badge visually anchored to the card's top edge
// as the font size (and therefore renderedHeight) grows - tuned by eye per
// size band, since the proportional offset alone drifts at larger sizes.
const TOP_OFFSET_BREAKPOINTS: Array<[maxFontSize: number, offset: number]> = [
    [14, 8],
    [20, 10],
    [25, 12],
    [30, 14],
    [34, 16],
    [38, 20],
];
const TOP_OFFSET_OVERFLOW = 22;

// The ribbon/bat chrome is built from exactly two seed colors - every other
// tone (gradient stops, strokes, shadows) is derived from these via
// lighten/darken. The skull's own bone-white shading is left untouched (it's
// fixed iconography, not ribbon chrome) except for its outline strokes.
const DEFAULT_PRIMARY_COLOR = "#111111";
const DEFAULT_SECONDARY_COLOR = "#f36a0a";

const estimateTextWidth = (text: string) => text.length * TEXT_FONT_SIZE * 0.55;

type DiscountSkullBadgeProps = {
    text: string;
    fontSize?: number;
    fontWeight?: string | number;
    fontStyle?: string;
    color?: string;
    primaryColor?: string;
    secondaryColor?: string;
    style?: CSSProperties;
    [dataAttr: `data-${string}`]: unknown;
};

const DiscountSkullBadge = ({
    text,
    fontSize,
    fontWeight,
    fontStyle,
    color,
    primaryColor,
    secondaryColor,
    style,
    ...rest
}: DiscountSkullBadgeProps) => {
    const uid = useId();
    const textRef = useRef<SVGTextElement>(null);
    const [textWidth, setTextWidth] = useState(() => estimateTextWidth(text || ""));

    const measure = () => {
        const node = textRef.current;
        if (!node) return;
        try {
            const box = node.getBBox();
            if (box.width > 0) setTextWidth(box.width);
        } catch {
            // node not measurable yet (e.g. not laid out) - keep last known width
        }
    };

    useLayoutEffect(() => {
        measure();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [text, fontWeight, fontStyle]);

    useEffect(() => {
        const fonts = (document as any)?.fonts;
        if (fonts?.ready) {
            fonts.ready.then(measure).catch(() => {});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [text, fontWeight, fontStyle]);

    if (!text) return null;

    const safeFontSize = fontSize && fontSize > 0 ? fontSize : BASE_FONT_SIZE;
    const fontScale = Math.min(MAX_FONT_SCALE, Math.max(MIN_FONT_SCALE, safeFontSize / BASE_FONT_SIZE));

    const delta = Math.max(0, textWidth + SIDE_PADDING * 2 - TEXT_SAFE_WIDTH);
    const designWidth = DESIGN_WIDTH + delta;

    const finalScale = DISPLAY_SCALE * fontScale;
    const renderedWidth = designWidth * finalScale;
    const renderedHeight = DESIGN_HEIGHT * finalScale;
    const textX = TEXT_CENTER_X + delta / 2;

    const shadowId = `skull-shadow-${uid}`;
    const ribbonId = `skull-ribbon-${uid}`;
    const orangeId = `skull-orange-${uid}`;
    const faceId = `skull-face-${uid}`;
    const sideId = `skull-side-${uid}`;

    const topOffset =
        TOP_OFFSET_BREAKPOINTS.find(([maxFontSize]) => safeFontSize <= maxFontSize)?.[1] ?? TOP_OFFSET_OVERFLOW;

    const primary = primaryColor || DEFAULT_PRIMARY_COLOR;
    const secondary = secondaryColor || DEFAULT_SECONDARY_COLOR;

    // Neutral family: bats, the ribbon body/shadow, and the skull's outline strokes.
    const primaryLight = lightenHexColor(primary, 8);
    const primaryDark = darkenHexColor(primary, 4);

    // Accent family: bat veins and the ribbon's orange gradient.
    const secondaryLight = lightenHexColor(secondary, 20);
    const secondaryDark = darkenHexColor(secondary, 15);

    return (
        <svg
            {...rest}
            width={renderedWidth}
            height={renderedHeight}
            viewBox={`0 0 ${designWidth} ${DESIGN_HEIGHT}`}
            style={{
                position: "absolute",
                top: -((CONTENT_TOP / DESIGN_HEIGHT) * renderedHeight + topOffset),
                right: 5,
                overflow: "visible",
                zIndex: 10,
                ...style,
            }}
            role="img"
            aria-label={text}
        >
            <defs>
                <linearGradient id={ribbonId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={primaryLight} />
                    <stop offset="45%" stopColor={primary} />
                    <stop offset="100%" stopColor={primaryDark} />
                </linearGradient>
                <linearGradient id={orangeId} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor={secondaryLight} />
                    <stop offset="50%" stopColor={secondary} />
                    <stop offset="100%" stopColor={secondaryDark} />
                </linearGradient>
                <radialGradient id={faceId} cx="35%" cy="25%" r="75%">
                    <stop offset="0%" stopColor="#ffffff" />
                    <stop offset="45%" stopColor="#eeeeee" />
                    <stop offset="78%" stopColor="#c8c8c8" />
                    <stop offset="100%" stopColor="#8f8f8f" />
                </radialGradient>
                <linearGradient id={sideId} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#777" />
                    <stop offset="100%" stopColor="#292929" />
                </linearGradient>
                <filter id={shadowId} x="-40%" y="-40%" width="180%" height="200%">
                    <feDropShadow dx="0" dy="6" stdDeviation="5" floodColor="#000" floodOpacity=".65" />
                </filter>
            </defs>

            {/* Left ornament: fixed size, anchored to the left cap */}
            <path
                d="M64 56C52 40 37 33 20 37C27 43 30 49 30 55C25 53 20 53 14 56C21 60 25 65 26 72C33 69 40 69 46 71C49 76 53 80 59 82L68 68Z"
                fill={primaryDark}
                stroke={primaryLight}
                strokeWidth={2}
            />
            <path d="M21 39c11-2 22 3 32 15" fill="none" stroke={secondary} strokeWidth={2} opacity={0.65} />

            {/* Ribbon body: stretches horizontally to fit the label, and grows a little
                vertically (evenly around the centerline) to give the boosted text room */}
            <path
                d={`M55 ${58 - BAND_GROWTH / 2}h${226 + delta}l-5 ${49 + BAND_GROWTH}H${59}z`}
                fill={primaryDark}
                opacity={0.8}
            />
            <path
                d={`M55 ${42 - BAND_GROWTH / 2}h${225 + delta}v${55 + BAND_GROWTH}H55z`}
                fill={`url(#${ribbonId})`}
                stroke={primaryDark}
                strokeWidth={3}
                filter={`url(#${shadowId})`}
            />
            <path
                d={`M60 ${47 - BAND_GROWTH / 2}h${215 + delta}v${45 + BAND_GROWTH}H60z`}
                fill={primaryDark}
                stroke={`url(#${orangeId})`}
                strokeWidth={5}
            />

            <text
                ref={textRef}
                x={textX}
                y={73}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={color || "#ffffff"}
                fontSize={TEXT_FONT_SIZE}
                fontWeight={fontWeight || 800}
                fontStyle={fontStyle || "normal"}
            >
                {text}
            </text>

            {/* Skull: fixed-size ornament pinned to the ribbon's right edge */}
            <g transform={`translate(${258 + delta} 39)`} filter={`url(#${shadowId})`}>
                <path
                    d="M39 4C19 4 5 17 5 35C5 48 11 57 20 63L20 73H57V63C67 57 73 48 73 35C73 17 59 4 39 4Z"
                    fill={`url(#${sideId})`}
                    stroke={primaryDark}
                    strokeWidth={4}
                />
                <path
                    d="M36 0C17 0 4 13 4 31C4 44 10 53 19 58L19 69H53V58C62 53 68 44 68 31C68 13 55 0 36 0Z"
                    fill={`url(#${faceId})`}
                    stroke={primary}
                    strokeWidth={3}
                />
                <ellipse cx="29" cy="12" rx="11" ry="5" fill="#fff" opacity={0.55} />
                <ellipse cx="24" cy="30" rx="9" ry="11" fill="#080808" />
                <ellipse cx="26" cy="28" rx="3" ry="4" fill="#303030" />
                <ellipse cx="48" cy="30" rx="9" ry="11" fill="#080808" />
                <ellipse cx="46" cy="28" rx="3" ry="4" fill="#303030" />
                <path d="M32 43L36 34L41 43L36 48Z" fill="#161616" />
                <path d="M10 42c5 7 10 10 16 12" fill="none" stroke="#999" strokeWidth={3} opacity={0.45} />
                <path d="M62 42c-5 7-10 10-16 12" fill="none" stroke="#777" strokeWidth={3} opacity={0.45} />
                <path
                    d="M19 57C25 61 30 63 36 63C42 63 48 61 53 57V70H19Z"
                    fill="#d4d4d4"
                    stroke="#777"
                    strokeWidth={1}
                />
                <path d="M24 56v10M30 58v11M36 58v11M42 58v11M48 56v10" stroke="#444" strokeWidth={2} />
                <path d="M21 55c8 5 22 5 30 0" fill="none" stroke="#222" strokeWidth={3} />
                <circle cx="18" cy="18" r="3" fill="#fff" opacity={0.75} />
            </g>

            {/* Right ornament: rigidly translated as the ribbon stretches */}
            <g transform={`translate(${delta} 0)`}>
                <path
                    d="M317 55C329 42 342 37 357 40C352 45 349 50 349 55C354 53 359 54 363 57C357 61 353 66 352 72C346 69 340 69 335 71C332 75 328 78 323 80L316 69Z"
                    fill={primaryDark}
                    stroke={primaryLight}
                    strokeWidth={2}
                />
                <path d="M354 42c-10-1-19 4-28 14" fill="none" stroke={secondary} strokeWidth={2} opacity={0.65} />
            </g>
        </svg>
    );
};

export default DiscountSkullBadge;
